/**
 * InlineDataCard — renders inline charts, tables, and metrics inside TERRA chat.
 *
 * Three types:
 * - inline_table: styled HTML table with headers and rows
 * - inline_chart: Recharts line/bar/area chart
 * - inline_metrics: KPI cards with status indicators
 */
import { useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import type { InlineComponent } from '@/api/terra';

interface Props {
  component: InlineComponent;
}

export function InlineDataCard({ component }: Props) {
  switch (component.type) {
    case 'inline_table':
      return <InlineTable {...component} />;
    case 'inline_chart':
      return <InlineChart {...component} />;
    case 'inline_metrics':
      return <InlineMetrics {...component} />;
    default:
      return null;
  }
}

// ── Inline Table ─────────────────────────────────────────────────────

function InlineTable({ title, headers, rows }: { title: string; headers: string[]; rows: any[][] }) {
  if (!rows.length) return null;

  return (
    <div className="mt-3 rounded-lg border border-mono-700/40 overflow-hidden">
      {title && (
        <div className="px-3 py-1.5 bg-mono-800/60 text-[11px] font-medium text-mono-300 border-b border-mono-700/40">
          {title}
        </div>
      )}
      <div className="overflow-x-auto max-h-[240px] overflow-y-auto">
        <table className="min-w-full text-[11px]">
          <thead className="bg-mono-800/40 sticky top-0">
            <tr>
              {headers.map((h, i) => (
                <th key={i} className="px-2 py-1.5 text-left font-medium text-mono-400 whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-t border-mono-800/30 hover:bg-mono-800/20">
                {row.map((cell, j) => (
                  <td key={j} className="px-2 py-1 text-mono-200 whitespace-nowrap">
                    {cell ?? '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length >= 20 && (
        <div className="px-3 py-1 text-[10px] text-mono-500 border-t border-mono-800/30">
          Showing first 20 rows
        </div>
      )}
    </div>
  );
}

// ── Inline Chart ─────────────────────────────────────────────────────

function InlineChart({ title, chart_type, series, x_label, y_label }: {
  title: string;
  chart_type: 'line' | 'bar' | 'area';
  series: { name: string; data: { x: string; y: number | null }[] }[];
  x_label?: string;
  y_label?: string;
}) {
  // Transform series data into Recharts format
  const chartData = useMemo(() => {
    if (!series.length || !series[0].data.length) return [];

    // Merge all series into unified data points keyed by x
    const dataMap = new Map<string, Record<string, any>>();
    for (const s of series) {
      for (const point of s.data) {
        if (point.y == null) continue;
        const existing = dataMap.get(point.x) || { x: point.x };
        existing[s.name] = point.y;
        dataMap.set(point.x, existing);
      }
    }
    return Array.from(dataMap.values()).sort((a, b) => a.x.localeCompare(b.x));
  }, [series]);

  if (!chartData.length) return null;

  const colors = ['#06b6d4', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

  // Format x-axis labels (shorten ISO dates)
  const formatX = (val: string) => {
    if (val.includes('T') || val.match(/^\d{4}-\d{2}-\d{2}/)) {
      return val.slice(0, 10);
    }
    return val;
  };

  const ChartComponent = chart_type === 'bar' ? BarChart : chart_type === 'area' ? AreaChart : LineChart;

  return (
    <div className="mt-3 rounded-lg border border-mono-700/40 overflow-hidden">
      {title && (
        <div className="px-3 py-1.5 bg-mono-800/60 text-[11px] font-medium text-mono-300 border-b border-mono-700/40">
          {title}
        </div>
      )}
      <div className="px-2 py-3">
        <ResponsiveContainer width="100%" height={200}>
          <ChartComponent data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
            <XAxis
              dataKey="x"
              tickFormatter={formatX}
              tick={{ fill: '#888', fontSize: 9 }}
              tickLine={false}
              axisLine={{ stroke: '#444' }}
            />
            <YAxis
              tick={{ fill: '#888', fontSize: 9 }}
              tickLine={false}
              axisLine={{ stroke: '#444' }}
              width={50}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '6px',
                fontSize: '11px',
                color: '#ddd',
              }}
              labelFormatter={formatX}
            />
            {series.map((s, i) => {
              const color = colors[i % colors.length];
              if (chart_type === 'bar') {
                return <Bar key={s.name} dataKey={s.name} fill={color} fillOpacity={0.8} radius={[2, 2, 0, 0]} />;
              }
              if (chart_type === 'area') {
                return <Area key={s.name} dataKey={s.name} stroke={color} fill={color} fillOpacity={0.15} strokeWidth={1.5} dot={false} />;
              }
              return <Line key={s.name} dataKey={s.name} stroke={color} strokeWidth={1.5} dot={{ r: 2, fill: color }} activeDot={{ r: 4 }} />;
            })}
          </ChartComponent>
        </ResponsiveContainer>
        {(x_label || y_label) && (
          <div className="flex justify-between px-2 mt-1 text-[9px] text-mono-500">
            <span>{x_label}</span>
            <span>{y_label}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Inline Metrics ───────────────────────────────────────────────────

function InlineMetrics({ title, metrics }: {
  title: string;
  metrics: { label: string; value: string | number; unit?: string; status?: string }[];
}) {
  if (!metrics.length) return null;

  const statusColors: Record<string, string> = {
    ok: 'text-green-400',
    warning: 'text-amber-400',
    critical: 'text-red-400',
  };

  return (
    <div className="mt-3 rounded-lg border border-mono-700/40 overflow-hidden">
      {title && (
        <div className="px-3 py-1.5 bg-mono-800/60 text-[11px] font-medium text-mono-300 border-b border-mono-700/40">
          {title}
        </div>
      )}
      <div className="grid grid-cols-2 gap-px bg-mono-700/30">
        {metrics.map((m, i) => (
          <div key={i} className="bg-mono-900/80 px-3 py-2.5">
            <div className="text-[10px] text-mono-500 mb-0.5">{m.label}</div>
            <div className={`text-sm font-medium ${statusColors[m.status || ''] || 'text-mono-100'}`}>
              {m.value}
              {m.unit && <span className="text-[10px] text-mono-500 ml-0.5">{m.unit}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
